import { useEffect, useState } from "react";
import {
  useCreateTeamMember,
  useCreateTeamOwner,
  useUpdateTeamMember,
  useUpdateTeamOwner,
} from "../../hooks/useTeam";
import type { TeamMemberRow, TeamOwner } from "../../lib/team";

type MemberDialogProps = {
  member?: TeamMemberRow;
  onClose: () => void;
};

export function MemberDialog({ member, onClose }: MemberDialogProps) {
  const create = useCreateTeamMember();
  const update = useUpdateTeamMember();
  const [name, setName] = useState(member?.name ?? "");
  const [rate, setRate] = useState(String(member?.hourly_rate ?? ""));
  const [phone, setPhone] = useState(member?.phone ?? "");
  const [email, setEmail] = useState(member?.email ?? "");
  const [active, setActive] = useState(member?.active ?? true);
  const busy = create.isPending || update.isPending;
  const failed = create.isError || update.isError;

  const save = () => {
    const body = {
      name: name.trim(),
      hourly_rate: Number(rate.replace(",", ".")) || 0,
      phone: phone.trim(),
      email: email.trim(),
      active,
    };
    if (member) update.mutate({ id: member.id, patch: body }, { onSuccess: onClose });
    else create.mutate(body, { onSuccess: onClose });
  };

  return (
    <Dialog title={member ? "Helfer bearbeiten" : "Neuer Helfer"} onClose={onClose}>
      <div className="team-form-grid">
        <Field label="Name"><input autoFocus value={name} onChange={(event) => setName(event.target.value)} /></Field>
        <Field label="Stundenlohn (€)"><input inputMode="decimal" value={rate} onChange={(event) => setRate(event.target.value)} /></Field>
        <Field label="Telefon"><input value={phone} onChange={(event) => setPhone(event.target.value)} /></Field>
        <Field label="E-Mail"><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field>
        <label className="team-check"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /> Aktiv</label>
      </div>
      {failed && <p className="team-error">Die Daten konnten nicht gespeichert werden.</p>}
      <DialogActions onClose={onClose} onSave={save} disabled={!name.trim() || busy} busy={busy} />
    </Dialog>
  );
}

type OwnerDialogProps = {
  owner?: TeamOwner;
  onClose: () => void;
};

export function OwnerDialog({ owner, onClose }: OwnerDialogProps) {
  const create = useCreateTeamOwner();
  const update = useUpdateTeamOwner();
  const [name, setName] = useState(owner?.name ?? "");
  const [phone, setPhone] = useState(owner?.phone ?? "");
  const [email, setEmail] = useState(owner?.email ?? "");
  const [active, setActive] = useState(owner?.active ?? true);
  const busy = create.isPending || update.isPending;

  const save = () => {
    const base = { name: name.trim(), phone: phone.trim(), email: email.trim() };
    if (owner) update.mutate({ id: owner.id, patch: { ...base, active } }, { onSuccess: onClose });
    else create.mutate(base, { onSuccess: onClose });
  };

  return (
    <Dialog title={owner ? "Inhaber bearbeiten" : "Neuer Inhaber"} onClose={onClose}>
      <div className="team-form-grid">
        <Field label="Name"><input autoFocus value={name} onChange={(event) => setName(event.target.value)} /></Field>
        <Field label="Telefon"><input value={phone} onChange={(event) => setPhone(event.target.value)} /></Field>
        <Field label="E-Mail"><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field>
        {owner && <label className="team-check"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /> Aktiv</label>}
      </div>
      {(create.isError || update.isError) && <p className="team-error">Die Daten konnten nicht gespeichert werden.</p>}
      <DialogActions onClose={onClose} onSave={save} disabled={!name.trim() || busy} busy={busy} />
    </Dialog>
  );
}

export function Dialog({ title, onClose, children }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  return (
    <div className="team-overlay" role="presentation" onMouseDown={onClose}>
      <section className="team-dialog" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <header><h2>{title}</h2><button className="team-icon-button" type="button" onClick={onClose} aria-label="Schließen">×</button></header>
        {children}
      </section>
    </div>
  );
}

export function Field({ label, children, full = false }: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return <label className={full ? "team-field team-field-full" : "team-field"}><span>{label}</span>{children}</label>;
}

/**
 * Fussleiste eines Dialogs.
 *
 * Ohne `onSave` bleibt nur ein Schliessen-Knopf: manche Dialoge speichern nicht
 * am Ende, sondern pro Zeile (z. B. die Bankzeilen-Auswahl) — dort waere ein
 * "Speichern" irrefuehrend.
 */
export function DialogActions({ onClose, onSave, disabled, busy }: {
  onClose: () => void;
  onSave?: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <footer className="team-dialog-actions">
      <button className="team-button secondary" type="button" onClick={onClose}>{onSave ? "Abbrechen" : "Schließen"}</button>
      {onSave && (
        <button className="team-button primary" type="button" disabled={disabled} onClick={onSave}>{busy ? "Speichert …" : "Speichern"}</button>
      )}
    </footer>
  );
}
